import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router'
import { ArrowLeft, Check, Pencil, Plus, Trash2, X } from 'lucide-react'
import { toast } from 'sonner'
import { StatePanel } from '@/components/common/StatePanel'
import { useOrganization } from '@/contexts/OrganizationContext'
import { prepacksApi } from '@/services/api'
import { PrepackCard, TemplateIframePreview } from './PrepackCard'
import { STARTER_TEMPLATES, TEMPLATE_SAMPLE_PACK, templateSamplePack, templateSampleTeamId } from './prepackCardModel'
import { useTemplateSampleTeams } from './useTemplateSampleTeams'
import { getTemplateVariant, normalizeTemplate, normalizeTemplates } from '../../../../../shared/pkg/prepacks/templateVariants.js'
import { usePrepackPreview } from './prepackPreviewContext'
import TemplateBuilder from './TemplateBuilder'
import { followPackUpdates, libraryPathFor } from './packUpdates'
import { brandingToCssVars, resolveBrandingWithOverrides } from '@feed-widget/branding'

const CSS_VARIABLE_REFERENCE_PATTERN = /^var\(--[\w-]+\)$/
const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}$/i

const NEW_TEMPLATE_CSS = STARTER_TEMPLATES.find((template) => template.id === 't-midnight').cssCode
const NEW_TEMPLATE_HTML = STARTER_TEMPLATES.find((template) => template.id === 't-midnight').htmlCode

function cloneTemplates(templates) {
  return structuredClone(normalizeTemplates(templates))
}

/** Brings a Default card hidden from the gallery (#982) back into it. */
function showHidden(templates) {
  return templates.map((template) => (template.isHidden ? { ...template, isHidden: false } : template))
}

function newTemplate() {
  return {
    ...structuredClone(STARTER_TEMPLATES.find((template) => template.id === 't-midnight')),
    id: `tpl-${globalThis.crypto?.randomUUID?.() || Date.now()}`,
    name: '',
    description: '',
    cssCode: NEW_TEMPLATE_CSS,
    htmlCode: NEW_TEMPLATE_HTML,
    isDefault: false,
    isFallback: false,
  }
}

export default function PrepacksConfigurationPage() {
  const { currentOrg, hasProductAccess } = useOrganization()
  const navigate = useNavigate()
  const slug = currentOrg?.slug
  const canConfigure = hasProductAccess('prepacks', 'configure')
  const queryClient = useQueryClient()
  const { branding } = usePrepackPreview()
  const [colorMode, setColorMode] = useState(() => branding.darkMode ? 'dark' : 'light')
  const [templates, setTemplates] = useState(null)
  const [configSnapshot, setConfigSnapshot] = useState(null)
  const [editing, setEditing] = useState(null)
  const closeEditor = () => setEditing(null)
  const [draggingId, setDraggingId] = useState(null)
  const [dropIndex, setDropIndex] = useState(null)
  const savingRef = useRef(false)

  const configQuery = useQuery({
    queryKey: ['prepacks', 'configuration', slug],
    queryFn: () => prepacksApi.getConfiguration(slug),
    enabled: Boolean(slug),
  })

  useEffect(() => {
    // Keep the editor and its version together when background queries refresh.
    if (!configQuery.data || editing) return
    setConfigSnapshot(configQuery.data)
    const saved = configQuery.data.card_templates || []
    setTemplates(cloneTemplates(saved))
  }, [configQuery.data, editing])

  // ?template=<id> (the schedule builder's "Edit template") opens that template's editor once, then leaves the URL.
  const [searchParams, setSearchParams] = useSearchParams()
  const linkedTemplate = searchParams.get('template')
  useEffect(() => {
    if (!linkedTemplate || !templates) return
    const found = templates.find((template) => template.id === linkedTemplate)
    if (found && canConfigure && !editing) setEditing(found)
    setSearchParams((params) => {
      const next = new URLSearchParams(params)
      next.delete('template')
      return next
    }, { replace: true })
  }, [linkedTemplate, templates, canConfigure, editing, setSearchParams])

  // Each gallery card draws the sample its template was saved with (#1121), on the team it names, so the
  // Templates page shows what the builder showed and not the Eagles for every card.
  const sampleTeams = useTemplateSampleTeams(slug, templates)
  const galleryPacks = useMemo(() => new Map((templates || []).map((template) => [
    template.id,
    { ...templateSamplePack(template, sampleTeams.get(templateSampleTeamId(template))), template_id: template.isFallback ? null : template.id },
  ])), [templates, sampleTeams])

  const saveConfigMutation = useMutation({
    // The page saves templates only. The organisation's title house style and odds band
    // (ai_title_*, min_odds, max_odds) are not edited in Studio since #1045 and ride along
    // unchanged from the snapshot: ValidateConfiguration needs real odds, and the title
    // service keeps reading them. Repeat limits are never sent; the API keeps the stored
    // defaults (#1057).
    mutationFn: (cardTemplates) => {
      const { repeat_limits: _repeats, ...stored } = configSnapshot
      return prepacksApi.saveConfiguration(slug, { ...stored, card_templates: normalizeTemplates(cardTemplates) })
    },
    onSuccess: (data) => {
      setConfigSnapshot(data)
      queryClient.setQueryData(['prepacks', 'configuration', slug], data)
    },
  })

  // `followTemplate` is the template whose packs the save's message follows
  // (#1089): how many use it, and the update repainting their pictures.
  const persistTemplates = async (nextTemplates, successMessage, { followTemplate = null } = {}) => {
    if (!canConfigure || savingRef.current) return false
    savingRef.current = true
    const previous = templates
    setTemplates(nextTemplates)
    try {
      const saved = await saveConfigMutation.mutateAsync(nextTemplates)
      const update = followTemplate ? (saved?.template_updates || []).find((item) => item.template_id === followTemplate) : null
      if (update) {
        const onView = () => navigate(libraryPathFor({ template: followTemplate }))
        // The update counts the template's packs itself, with the pictures it repaints.
        if (update.propagation_id) void followPackUpdates({ slug, ids: [update.propagation_id], saved: successMessage, queryClient, onView })
        else toast.success(`${successMessage}. ${update.packs} ${update.packs === 1 ? 'pack' : 'packs'} updated`, { action: { label: 'View', onClick: onView } })
      } else if (successMessage) {
        toast.success(successMessage)
      }
      return true
    } catch (error) {
      setTemplates(previous)
      if (error?.response?.status === 409) {
        void queryClient.invalidateQueries({ queryKey: ['prepacks', 'configuration', slug] })
        toast.error(editing ? 'Templates changed elsewhere. Cancel and reopen the editor before saving.' : 'Templates changed elsewhere. Refresh the library before trying again.')
      } else {
        toast.error(error?.response?.data?.error || 'Failed to save templates')
      }
      return false
    } finally {
      savingRef.current = false
    }
  }

  const saveTemplate = async (template) => {
    const existing = templates.some((item) => item.id === template.id)
    const normalized = template.isDefault
      ? templates.map((item) => ({ ...item, isDefault: false }))
      : templates
    const next = existing
      ? normalized.map((item) => (item.id === template.id ? template : item))
      : [...normalized, template]
    if (await persistTemplates(next, 'Template saved', { followTemplate: template.id })) closeEditor()
  }

  // The Default card draws every pack without artwork, so Delete takes it out of the gallery
  // (isHidden), not out of the catalog, and only while an artwork template is the default: the
  // card in effect is always on the page (#982). Show Default card brings it back.
  const deleteTemplate = async (id) => {
    if (!canConfigure) return
    const target = templates.find((template) => template.id === id)
    if (!target) return
    if (target.isFallback) {
      if (!templates.some((template) => template.isDefault && !template.isFallback)) {
        toast.error('Set an artwork template as the default before deleting the Default card')
        return
      }
      const next = templates.map((template) => (template.isFallback ? { ...template, isHidden: true } : template))
      if (await persistTemplates(next, 'Default card hidden. Packs without artwork still use it.')) closeEditor()
      return
    }
    let next = templates.filter((template) => template.id !== id)
    if (!next.some((template) => !template.isFallback)) {
      toast.error('Keep at least one template')
      return
    }
    // Deleting the default leaves the Default card as the default, so a hidden one comes back.
    if (!next.some((template) => template.isDefault && !template.isFallback)) next = showHidden(next)
    if (await persistTemplates(next, 'Template deleted')) closeEditor()
  }

  const showFallback = () => {
    void persistTemplates(showHidden(templates), 'Default card restored')
  }

  // The Default card is the default when no artwork template is: choosing it clears every flag.
  const setDefault = (id) => {
    const fallback = templates.find((template) => template.id === id)?.isFallback
    const next = templates.map((template) => ({ ...template, isDefault: !fallback && template.id === id }))
    void persistTemplates(next, 'Default template updated')
  }
  const isDefaultTemplate = (template) => (template.isFallback ? !templates.some((item) => item.isDefault && !item.isFallback) : Boolean(template.isDefault))

  // The gallery: a hidden Default card stays in the catalog behind the cards.
  const cards = templates?.filter((template) => !template.isHidden) ?? []
  const hiddenFallback = templates?.some((template) => template.isHidden) ?? false

  // The saved order is the gallery order; nothing is pinned, the Default card included (#982).
  const moveTemplate = (id, index) => {
    if (!canConfigure || savingRef.current || index < 0 || index >= cards.length) return
    const from = cards.findIndex((template) => template.id === id)
    if (from < 0 || from === index) return
    const next = [...cards]
    const [moved] = next.splice(from, 1)
    next.splice(index, 0, moved)
    // The cards fill the visible slots, so a hidden Default card comes back where it was.
    let slot = 0
    void persistTemplates(templates.map((template) => (template.isHidden ? template : next[slot++])))
  }

  // New templates and templates made in the builder open in the builder (its
  // Code tab covers hand edits); templates authored before it open in the code editor.
  if (editing && (editing === 'new' || editing.builder)) {
    return (
      <TemplateBuilder
        template={editing === 'new' ? null : editing}
        orgSlug={slug}
        canConfigure={canConfigure}
        isSaving={saveConfigMutation.isPending}
        colorMode={colorMode}
        onColorModeChange={setColorMode}
        onSave={saveTemplate}
        onCancel={closeEditor}
      />
    )
  }

  if (editing) {
    return (
      <TemplateEditor
        template={editing === 'new' ? null : editing}
        canConfigure={canConfigure}
        isSaving={saveConfigMutation.isPending}
        colorMode={colorMode}
        onColorModeChange={setColorMode}
        onSave={saveTemplate}
        onDelete={deleteTemplate}
        onCancel={closeEditor}
      />
    )
  }

  return (
    <div className="flex h-full flex-1 flex-col overflow-hidden bg-[var(--prepacks-bg)] text-[var(--prepacks-text)]">
      <header className="flex shrink-0 items-center justify-between gap-4 border-b border-[var(--prepacks-border)] bg-[var(--prepacks-surface)] px-5 py-5 sm:px-8">
        <div>
          <h1 className="text-xl font-semibold">Templates</h1>
          <p className="mt-0.5 text-xs text-[var(--prepacks-muted)]">
            {templates?.filter((template) => !template.isFallback).length || 0} art templates · native Default card styles packs without artwork{hiddenFallback ? ' (hidden here)' : ''}
          </p>
        </div>
        <ColorModeSelector colorMode={colorMode} onChange={setColorMode} />
        {canConfigure ? (
          <div className="flex items-center gap-2">
            {hiddenFallback ? (
              <button type="button" onClick={showFallback} disabled={saveConfigMutation.isPending} className="rounded-lg border border-[var(--prepacks-border)] px-3 py-2 text-xs font-medium text-[var(--prepacks-accent-text)] transition-colors hover:bg-[var(--prepacks-raised)] disabled:opacity-50">
                Show Default card
              </button>
            ) : null}
            <button type="button" onClick={() => setEditing('new')} disabled={!templates || configQuery.isError || saveConfigMutation.isPending} className="flex items-center gap-1.5 rounded-lg bg-[var(--prepacks-accent)] px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-[var(--prepacks-accent-hover)] disabled:opacity-50">
              <Plus className="h-3.5 w-3.5" /> New template
            </button>
          </div>
        ) : null}
      </header>

      <section className="flex-1 overflow-y-auto px-5 py-6 sm:px-8 sm:py-7">
        {configQuery.isLoading || (templates == null && !configQuery.isError) ? (
          <StatePanel state="loading" title="Loading templates" description="Retrieving this organization’s card templates." />
        ) : configQuery.isError ? (
          <StatePanel state="error" title="Templates could not be loaded" description={configQuery.error?.response?.data?.error || 'Try loading the template library again.'} onRetry={() => configQuery.refetch()} />
        ) : (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {cards.map((template, index) => (
              <article
                key={template.id}
                draggable={canConfigure && !saveConfigMutation.isPending}
                onDragStart={(event) => { event.dataTransfer.effectAllowed = 'move'; setDraggingId(template.id) }}
                onDragEnd={() => { setDraggingId(null); setDropIndex(null) }}
                onDragOver={(event) => { if (canConfigure) { event.preventDefault(); setDropIndex(index) } }}
                onDrop={(event) => { event.preventDefault(); moveTemplate(draggingId, index); setDraggingId(null); setDropIndex(null) }}
                className={`group overflow-hidden rounded-2xl border bg-[var(--prepacks-surface)] transition-all ${draggingId === template.id ? 'scale-[.98] opacity-40' : ''} ${dropIndex === index && draggingId && draggingId !== template.id ? 'border-[var(--prepacks-accent-text)] ring-2 ring-[var(--prepacks-accent-text)]/25' : 'border-[var(--prepacks-border)] hover:border-[var(--prepacks-accent-text)]/50'}`}
              >
                <div className="w-full bg-[var(--prepacks-bg)] p-3">
                  <PrepackCard pack={galleryPacks.get(template.id)} templates={[template]} colorMode={colorMode} showActions={false} />
                </div>
                <div className="flex items-start justify-between gap-3 border-t border-[var(--prepacks-border)] px-4 py-3.5">
                  <div className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold text-[var(--prepacks-text)]">{template.name}</span>
                      {isDefaultTemplate(template) || template.isFallback ? <span className="shrink-0 rounded border border-[var(--prepacks-border)] bg-[var(--prepacks-raised)] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[var(--prepacks-accent-text)]">{isDefaultTemplate(template) ? 'Default' : 'Native'}</span> : null}
                    </span>
                    <span className="mt-0.5 block truncate text-xs text-[var(--prepacks-muted)]">{template.description}</span>
                    <span className="mt-2 flex gap-1.5">
                      {getTemplateVariant(template, colorMode).cssVars.filter((variable) => variable.type === 'color').slice(0, 6).map((variable) => (
                        <span key={variable.key} title={variable.label} className="h-3.5 w-3.5 rounded-full border border-[var(--prepacks-border)]" style={{ backgroundColor: variable.value }} />
                      ))}
                    </span>
                  </div>
                  <div className="flex shrink-0 flex-col gap-1">
                    {/* The one way into the editor. It stays visible at rest; the actions under it reveal on hover (#814). */}
                    <button type="button" onClick={() => setEditing(template)} aria-label={`Edit ${template.name}`} className="flex items-center justify-center gap-1 rounded border border-[var(--prepacks-border)] px-2 py-1 text-xs font-medium text-[var(--prepacks-accent-text)] transition-colors hover:border-[var(--prepacks-accent-text)] hover:bg-[var(--prepacks-raised)]">
                      <Pencil className="h-3 w-3" aria-hidden="true" />Edit
                    </button>
                    {canConfigure ? (
                      <div className="flex flex-col gap-1 opacity-100 transition-opacity lg:opacity-0 lg:group-hover:opacity-100 lg:group-focus-within:opacity-100">
                        <button type="button" onClick={() => setDefault(template.id)} disabled={isDefaultTemplate(template) || saveConfigMutation.isPending} className="rounded border border-[var(--prepacks-border)] px-2 py-0.5 text-[10px] text-[var(--prepacks-muted)] transition-colors hover:border-[var(--prepacks-accent-text)] hover:text-[var(--prepacks-accent-text)] disabled:opacity-50">{isDefaultTemplate(template) ? 'Default' : 'Set default'}</button>
                        <button type="button" onClick={() => deleteTemplate(template.id)} disabled={saveConfigMutation.isPending} className="rounded border border-[var(--prepacks-border)] px-2 py-0.5 text-[10px] text-[var(--wf-color-danger)] disabled:opacity-50">Delete</button>
                        <div className="flex gap-1">
                          <button type="button" aria-label={`Move ${template.name} earlier`} onClick={() => moveTemplate(template.id, index - 1)} disabled={index === 0 || saveConfigMutation.isPending} className="rounded border border-[var(--prepacks-border)] px-2 py-0.5 text-xs disabled:opacity-30">↑</button>
                          <button type="button" aria-label={`Move ${template.name} later`} onClick={() => moveTemplate(template.id, index + 1)} disabled={index === cards.length - 1 || saveConfigMutation.isPending} className="rounded border border-[var(--prepacks-border)] px-2 py-0.5 text-xs disabled:opacity-30">↓</button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

const INPUT_CLASS = 'w-full min-w-0 rounded border border-[var(--prepacks-border)] bg-[var(--prepacks-bg)] px-2.5 py-2 text-xs text-[var(--prepacks-text)] outline-none focus:border-[var(--prepacks-accent-text)] disabled:opacity-60'

const CTA_FIELDS = [
  ['CTA background', 'components.cta.background', 'color'],
  ['CTA text color', 'components.cta.color', 'color'],
  ['CTA border color', 'components.cta.borderColor', 'color'],
  ['CTA border width', 'components.cta.borderWidth'],
  ['CTA corner radius', 'layout.cta.borderRadius'],
  ['CTA horizontal padding', 'layout.cta.paddingX'],
  ['CTA vertical padding', 'layout.cta.paddingY'],
  ['CTA font size', 'typography.cta.fontSize'],
]
const ODDS_FIELDS = [
  ['Odds background', 'components.prepackOdds.background', 'color'],
  ['Odds text color', 'components.prepackOdds.color', 'color'],
  ['Odds border color', 'components.prepackOdds.borderColor', 'color'],
  ['Odds border width', 'components.prepackOdds.borderWidth'],
  ['Odds corner radius', 'components.prepackOdds.borderRadius'],
  ['Odds horizontal padding', 'components.prepackOdds.paddingX'],
  ['Odds vertical padding', 'components.prepackOdds.paddingY'],
  ['Odds font size', 'typography.odds.fontSize'],
]

function ColorModeSelector({ colorMode, onChange }) {
  return (
    <div className="flex shrink-0 items-center gap-1 rounded-lg border border-[var(--prepacks-border)] bg-[var(--prepacks-bg)] p-1" role="group" aria-label="Preview color mode">
      {['light', 'dark'].map((mode) => (
        <button key={mode} type="button" aria-pressed={mode === colorMode} onClick={() => onChange(mode)} className={`rounded px-3 py-1.5 text-xs font-medium ${mode === colorMode ? 'bg-[var(--prepacks-raised)] text-[var(--prepacks-accent-text)]' : 'text-[var(--prepacks-muted)]'}`}>
          {mode === 'light' ? 'Light' : 'Dark'}
        </button>
      ))}
    </div>
  )
}

function StyleValueInput({ label, value = '', type, disabled, onChange, placeholder, cssVars = {} }) {
  const inputID = useId()
  let pickerValue = value || placeholder || ''
  const seen = new Set()
  while (CSS_VARIABLE_REFERENCE_PATTERN.test(pickerValue) && !seen.has(pickerValue)) {
    seen.add(pickerValue)
    pickerValue = cssVars[pickerValue.slice(4, -1)] || ''
  }
  return (
    <div className="space-y-1.5 text-xs text-[var(--prepacks-text)]">
      <label htmlFor={inputID} className="block" title={type === 'color' && value.includes('var(') ? `Linked to ${value.includes('--feed-') ? 'destination branding' : 'artwork colors'}` : undefined}>{label}</label>
      <span className="flex items-center gap-2">
        {type === 'color' && HEX_COLOR_PATTERN.test(pickerValue) ? <input aria-label={`${label} picker`} type="color" value={pickerValue} disabled={disabled} onChange={(event) => onChange(event.target.value)} className="h-8 w-8 shrink-0 cursor-pointer rounded border border-[var(--prepacks-border)] bg-transparent disabled:opacity-60" /> : null}
        <input id={inputID} aria-label={label} value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} className={INPUT_CLASS} />
      </span>
    </div>
  )
}

function TemplateEditor({ template, colorMode, onColorModeChange, canConfigure, isSaving, onSave, onDelete, onCancel }) {
  const [draft, setDraft] = useState(() => structuredClone(normalizeTemplate(template || newTemplate())))
  const [activeTab, setActiveTab] = useState('styles')
  const { branding } = usePrepackPreview()
  const variant = draft.variants[colorMode]
  const nativeCssVars = {
    ...brandingToCssVars(resolveBrandingWithOverrides(branding, variant.branding)),
    ...Object.fromEntries(variant.cssVars.map((variable) => [variable.key, variable.value])),
  }
  const disabled = !canConfigure || isSaving
  const getPath = (source, path) => path.split('.').reduce((value, key) => value?.[key], source)

  const updateVariant = (update) => {
    if (disabled) return
    setDraft((current) => ({
      ...current,
      variants: { ...current.variants, [colorMode]: update(current.variants[colorMode]) },
    }))
  }
  const updateVariable = (index, patch) => updateVariant((current) => ({
    ...current,
    cssVars: current.cssVars.map((variable, variableIndex) => variableIndex === index ? { ...variable, ...patch } : variable),
  }))
  const updateBranding = (path, value) => updateVariant((current) => {
    const nextBranding = structuredClone(current.branding)
    const keys = path.split('.')
    let group = nextBranding
    for (const key of keys.slice(0, -1)) {
      group[key] ||= {}
      group = group[key]
    }
    if (value === '') delete group[keys.at(-1)]
    else group[keys.at(-1)] = value
    return { ...current, branding: nextBranding }
  })
  const addVariable = () => updateVariant((current) => {
    let index = current.cssVars.length + 1
    while (current.cssVars.some((variable) => variable.key === `--custom-${index}`)) index += 1
    return { ...current, cssVars: [...current.cssVars, { key: `--custom-${index}`, label: `Custom ${index}`, value: '#ffffff', type: 'color' }] }
  })
  const save = () => {
    if (disabled) return
    if (!draft.name.trim()) {
      toast.error('Enter a template name')
      return
    }
    // Markup edited by hand no longer matches the builder's choices, so it stops being a builder template.
    const { builder: _builder, ...authored } = draft
    void onSave({ ...authored, name: draft.name.trim(), description: draft.description.trim() })
  }
  const nativeFields = [
    ...(draft.isFallback ? [['Card background', 'colors.surface', 'color']] : []),
    ['Footer background', 'components.prepackActions.background', 'color'],
    ['Footer border color', 'components.prepackActions.borderColor', 'color'],
  ]
  const renderStyleFields = (fields) => fields.map(([label, path, type]) => (
    <StyleValueInput key={path} label={label} type={type} value={getPath(variant.branding, path) || ''} placeholder={getPath(branding, path) || 'Inherited'} cssVars={nativeCssVars} disabled={disabled} onChange={(value) => updateBranding(path, value)} />
  ))

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-y-auto bg-[var(--prepacks-bg)] text-[var(--prepacks-text)] lg:flex-row lg:overflow-hidden">
      <h1 className="sr-only">{template ? `Edit ${template.name}` : 'New template'}</h1>
      <section className="flex min-h-0 w-full shrink-0 flex-col border-b border-[var(--prepacks-border)] bg-[var(--prepacks-surface)] lg:h-full lg:w-[460px] lg:border-b-0 lg:border-r">
        <header className="flex shrink-0 items-center gap-3 border-b border-[var(--prepacks-border)] px-4 py-3.5">
          <button type="button" onClick={onCancel} className="rounded p-1 text-[var(--prepacks-muted)]" aria-label="Back to templates"><ArrowLeft className="h-4 w-4" /></button>
          <input aria-label="Template name" value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} disabled={disabled || draft.isFallback} placeholder="Template name" className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-[var(--prepacks-text)] outline-none placeholder:text-[var(--prepacks-muted)] disabled:opacity-70" />
          <button type="button" onClick={onCancel} className="rounded border border-[var(--prepacks-border)] px-3 py-1.5 text-xs">Cancel</button>
          <button type="button" onClick={save} disabled={disabled} className="rounded bg-[var(--prepacks-accent)] px-4 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[var(--prepacks-accent-hover)] disabled:opacity-50">{isSaving ? 'Saving…' : 'Save'}</button>
        </header>
        <div className="space-y-3 border-b border-[var(--prepacks-border)] px-5 py-3">
          <label className="block space-y-1 text-xs" htmlFor="template-description">
            <span>Description</span>
            <input id="template-description" value={draft.description} onChange={(event) => setDraft({ ...draft, description: event.target.value })} disabled={disabled} placeholder="When operators should use this style" className={INPUT_CLASS} />
          </label>
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-[var(--prepacks-muted)]">Editing {colorMode} appearance</span>
            <ColorModeSelector colorMode={colorMode} onChange={onColorModeChange} />
          </div>
          <p className="text-xs text-[var(--prepacks-muted)]">Both variants save together. The surface color mode selects the delivered variant; this preview does not change the Studio theme or surface mode.</p>
        </div>
        {!draft.isFallback ? (
          <div className="flex shrink-0 gap-4 border-b border-[var(--prepacks-border)] px-5" role="tablist" aria-label="Template settings">
            {[['styles', 'Appearance'], ['advanced', 'Advanced']].map(([key, label]) => (
              <button key={key} type="button" role="tab" aria-selected={activeTab === key} onClick={() => setActiveTab(key)} className={`border-b-2 py-2.5 text-xs font-medium ${activeTab === key ? 'border-[var(--prepacks-accent-text)] text-[var(--prepacks-accent-text)]' : 'border-transparent text-[var(--prepacks-muted)]'}`}>{label}</button>
            ))}
          </div>
        ) : null}
        <div className="min-h-[280px] flex-1 space-y-6 overflow-y-auto p-5">
          {activeTab === 'styles' || draft.isFallback ? (
            <>
              {!draft.isFallback && variant.cssVars.length > 0 ? (
                <fieldset className="space-y-3">
                  <legend className="mb-3 text-sm font-semibold">Artwork colors</legend>
                  <p className="text-xs text-[var(--prepacks-muted)]">The authored palette also styles linked footer, CTA, and odds colors.</p>
                  {variant.cssVars.filter((variable) => variable.type === 'color').map((variable) => (
                    <StyleValueInput key={variable.key} label={variable.label} value={variable.value} type={variable.type} disabled={disabled} onChange={(value) => updateVariable(variant.cssVars.indexOf(variable), { value })} />
                  ))}
                </fieldset>
              ) : null}
              <fieldset className="space-y-3">
                <legend className="mb-3 text-sm font-semibold">{draft.isFallback ? 'Native card and footer' : 'Native footer'}</legend>
                <p className="text-xs text-[var(--prepacks-muted)]">{draft.isFallback ? 'Styles cards with no artwork template assigned.' : 'Override these only for intentional contrast with the artwork. Artwork backgrounds are edited above.'} Empty values inherit destination branding.</p>
                {renderStyleFields(nativeFields)}
              </fieldset>
              <fieldset className="grid grid-cols-2 gap-3">
                <legend className="mb-3 text-sm font-semibold">CTA button</legend>
                {renderStyleFields(CTA_FIELDS)}
              </fieldset>
              <fieldset className="grid grid-cols-2 gap-3">
                <legend className="mb-3 text-sm font-semibold">Odds badge</legend>
                {renderStyleFields(ODDS_FIELDS)}
              </fieldset>
              <p className="text-xs text-[var(--prepacks-muted)]">Sizes accept CSS units such as 12px. Odds come from the pack selections; the betting action is controlled by the destination, not the template.</p>
            </>
          ) : (
            <>
              <fieldset className="space-y-3">
                <legend className="mb-3 text-sm font-semibold">Variable definitions · {colorMode}</legend>
                <p className="text-xs text-[var(--prepacks-muted)]">Variables belong to the selected mode. Shared HTML/CSS applies to both modes.</p>
                {variant.cssVars.map((variable, index) => (
                  <div key={index} className="space-y-2 rounded-lg border border-[var(--prepacks-border)] p-3">
                    <div className="flex gap-2">
                      <input value={variable.label} onChange={(event) => updateVariable(index, { label: event.target.value })} disabled={disabled} aria-label={`Variable label ${index + 1}`} className={INPUT_CLASS} />
                      <button type="button" disabled={disabled} onClick={() => updateVariant((current) => ({ ...current, cssVars: current.cssVars.filter((_, itemIndex) => itemIndex !== index) }))} className="text-[var(--wf-color-danger)] disabled:opacity-50" aria-label={`Remove ${variable.label}`}><X className="h-3.5 w-3.5" /></button>
                    </div>
                    <input aria-label={`CSS variable name ${index + 1}`} value={variable.key} onChange={(event) => updateVariable(index, { key: event.target.value })} disabled={disabled} className={INPUT_CLASS} />
                    <select aria-label={`Variable type ${index + 1}`} value={variable.type} onChange={(event) => updateVariable(index, { type: event.target.value })} disabled={disabled} className={INPUT_CLASS}><option value="color">Color</option><option value="text">Text</option></select>
                    <StyleValueInput label={`${variable.label} value`} value={variable.value} type={variable.type} disabled={disabled} onChange={(value) => updateVariable(index, { value })} />
                  </div>
                ))}
                <button type="button" disabled={disabled} onClick={addVariable} className="flex items-center gap-1 text-xs text-[var(--prepacks-accent-text)] disabled:opacity-50"><Plus className="h-3 w-3" />Add variable</button>
              </fieldset>
              {[
                ['Shared HTML', draft.htmlCode, (value) => setDraft({ ...draft, htmlCode: value })],
                ['Shared CSS', draft.cssCode, (value) => setDraft({ ...draft, cssCode: value })],
                [`${colorMode === 'light' ? 'Light' : 'Dark'} variant CSS`, variant.cssCode, (value) => updateVariant((current) => ({ ...current, cssCode: value }))],
              ].map(([label, value, update]) => (
                <label key={label} className="block space-y-2 text-xs font-semibold">
                  <span>{label}</span>
                  <textarea aria-label={label} value={value} onChange={(event) => update(event.target.value)} disabled={disabled} spellCheck={false} className={`${INPUT_CLASS} min-h-[200px] font-mono font-normal leading-relaxed`} />
                </label>
              ))}
              <details className="rounded border border-[var(--prepacks-border)] p-3">
                <summary className="cursor-pointer text-xs">Raw artwork preview</summary>
                <p className="my-2 text-xs text-[var(--prepacks-muted)]">Authored sample controls may appear here. Public cards use one native quote and CTA.</p>
                <TemplateIframePreview template={draft} pack={TEMPLATE_SAMPLE_PACK} colorMode={colorMode} height={300} />
              </details>
            </>
          )}
        </div>
        {template && !draft.isHidden && canConfigure ? (
          <div className="shrink-0 border-t border-[var(--prepacks-border)] p-4">
            <button type="button" onClick={() => onDelete(draft.id)} disabled={isSaving} className="flex items-center gap-1.5 text-xs text-[var(--wf-color-danger)] disabled:opacity-50"><Trash2 className="h-3.5 w-3.5" />Delete template</button>
          </div>
        ) : null}
      </section>
      <section className="flex min-h-[520px] flex-1 flex-col items-center overflow-y-auto p-6 lg:p-8">
        <h2 className="mb-5 text-xs font-semibold uppercase tracking-wider text-[var(--prepacks-muted)]">Live composed card · {colorMode}</h2>
        <div className="w-full max-w-[412px] rounded-2xl border border-[var(--prepacks-border)] bg-[var(--prepacks-bg)] p-4">
          <PrepackCard pack={{ ...TEMPLATE_SAMPLE_PACK, template_id: draft.isFallback ? null : draft.id }} templates={[draft]} colorMode={colorMode} showActions={false} />
        </div>
        <div className="mt-5 w-full max-w-[500px] rounded-xl border border-[var(--prepacks-border)] bg-[var(--prepacks-surface)] p-4">
          {draft.isFallback ? <p className="text-xs text-[var(--prepacks-muted)]">Default card applies automatically when a pack has no artwork template. Deleting it hides it from this page; packs without artwork still use it, and it cannot be selected as generation artwork.</p> : <>
            <p className="text-xs font-semibold">Apply this template when creating</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {['Create', 'Schedules'].map((scope) => <span key={scope} className="flex items-center gap-1.5 rounded-full border border-[var(--prepacks-border)] bg-[var(--prepacks-raised)] px-3 py-1 text-[11px]"><Check className="h-3 w-3 text-[var(--prepacks-accent-text)]" />{scope}</span>)}
            </div>
          </>}
        </div>
      </section>
    </div>
  )
}
